// src/services/socket.js - WebSocket with exponential backoff reconnection
import { io } from 'socket.io-client'
import { toast } from 'react-hot-toast'

class SocketService {
    constructor() {
        this.socket = null
        this.isConnected = false
        this.isConnecting = false
        this.isAuthenticated = false
        this.reconnectAttempts = 0
        this.maxReconnectAttempts = 10
        this.baseReconnectDelay = 1000
        this.maxReconnectDelay = 30000
        this.eventListeners = new Map()
        this.connectDebounceTimeout = null
        this.reconnectTimeout = null
        this.authToken = null
    }

    connect(authToken = null) {
        if (this.isConnected || this.isConnecting) {
            return Promise.resolve(this.socket)
        }

        if (this.connectDebounceTimeout) {
            clearTimeout(this.connectDebounceTimeout)
        }

        return new Promise((resolve, reject) => {
            this.connectDebounceTimeout = setTimeout(() => {
                this._connect(authToken, resolve, reject)
            }, 100)
        })
    }

    _getReconnectDelay() {
        // Exponential backoff with jitter: delay = min(base * 2^attempt + jitter, max)
        const exponential = this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts)
        const jitter = Math.random() * 1000
        return Math.min(exponential + jitter, this.maxReconnectDelay)
    }

    _connect(authToken = null, resolve, reject) {
        this.isConnecting = true
        this.authToken = authToken || this.authToken || localStorage.getItem('authToken')

        const socketUrl = process.env.NODE_ENV === 'production'
            ? window.location.origin
            : `http://${window.location.hostname}:3100`

        console.log('Connecting to socket server:', socketUrl)

        this.socket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            timeout: 10000,
            autoConnect: true,
            // Disable built-in reconnection — we handle it manually with exponential backoff
            reconnection: false
        })

        this.setupEventHandlers()

        this.socket.on('connect', () => {
            this.isConnected = true
            this.isConnecting = false
            this.reconnectAttempts = 0
            console.log('Connected to device server')

            // Authenticate immediately after connection if we have a token
            if (this.authToken) {
                this.authenticate(this.authToken)
            }

            this.emit('socketConnected')
            resolve(this.socket)
        })

        this.socket.on('connect_error', (error) => {
            this.isConnecting = false
            console.error('Socket connection error:', error.message)
            this._scheduleReconnect(reject)
        })
    }

    _scheduleReconnect(reject) {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error(`Socket: max reconnect attempts (${this.maxReconnectAttempts}) reached`)
            toast.error('Unable to connect to device server. Please refresh the page.')
            if (reject) reject(new Error('Max reconnect attempts reached'))
            return
        }

        this.reconnectAttempts++
        const delay = this._getReconnectDelay()
        console.log(`Socket: reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

        if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout)

        this.reconnectTimeout = setTimeout(() => {
            if (!this.isConnected && this.socket) {
                this.socket.connect()
            }
        }, delay)
    }

    setupEventHandlers() {
        this.socket.on('disconnect', (reason) => {
            this.isConnected = false
            this.isConnecting = false
            this.isAuthenticated = false
            console.log('Socket disconnected:', reason)
            this.emit('socketDisconnected', reason)

            // Auto-reconnect on all disconnect reasons except explicit client disconnect
            if (reason !== 'io client disconnect') {
                this._scheduleReconnect()
            }
        })

        // Handle authentication responses
        this.socket.on('authSuccess', (data) => {
            this.isAuthenticated = true
            console.log('Socket authenticated successfully:', data)
            toast.success('Connected to device server')
            this.emit('authenticated', data)
        })

        this.socket.on('authError', (data) => {
            this.isAuthenticated = false
            console.error('Socket authentication failed:', data)
            toast.error('Failed to authenticate with device server')
            this.emit('authError', data)
        })

        this.socket.on('deviceUpdate', (device) => {
            this.emit('deviceUpdate', device)
        })

        this.socket.on('hubUpdate', (hub) => {
            this.emit('hubUpdate', hub)
        })

        this.socket.on('error', (error) => {
            console.error('Socket error:', error)
            toast.error('Device communication error')
        })

        this.socket.on('emergencyStop', (data) => {
            console.log('Emergency stop received:', data)
            toast.error(`Emergency stop activated: ${data.reason}`, {
                duration: 6000,
                style: {
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: '2px solid #dc2626'
                }
            })
            this.emit('emergencyStop', data)
        })
    }

    authenticate(token = null) {
        if (!this.socket || !this.isConnected) {
            console.warn('Cannot authenticate: socket not connected')
            return
        }

        const authToken = token || this.authToken || localStorage.getItem('authToken')

        if (!authToken) {
            console.warn('No authentication token available')
            return
        }

        this.authToken = authToken
        console.log('Authenticating socket connection...')
        this.socket.emit('authenticate', { token: authToken })
    }

    setAuthToken(token) {
        this.authToken = token
        if (this.isConnected && !this.isAuthenticated) {
            this.authenticate(token)
        }
    }

    disconnect() {
        if (this.connectDebounceTimeout) clearTimeout(this.connectDebounceTimeout)
        if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout)

        if (this.socket) {
            this.socket.disconnect()
            this.socket = null
        }

        this.isConnected = false
        this.isConnecting = false
        this.isAuthenticated = false
        this.reconnectAttempts = 0
        this.authToken = null
    }

    // Event handling
    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set())
        }
        this.eventListeners.get(event).add(callback)

        if (this.socket) {
            this.socket.on(event, callback)
        }
    }

    off(event, callback) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).delete(callback)
        }

        if (this.socket) {
            this.socket.off(event, callback)
        }
    }

    emit(event, data) {
        // Emit to internal listeners
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach(callback => {
                try {
                    callback(data)
                } catch (error) {
                    console.error('Error in event listener:', error)
                }
            })
        }

        // Emit to socket if connected
        if (this.socket && this.isConnected) {
            this.socket.emit(event, data)
        }
    }

    isUserAuthenticated() {
        return this.isConnected && this.isAuthenticated
    }

    getConnectionStatus() {
        return {
            connected: this.isConnected,
            authenticated: this.isAuthenticated,
            connecting: this.isConnecting,
            reconnectAttempts: this.reconnectAttempts
        }
    }
}

// Create singleton instance
const socketService = new SocketService()

export default socketService
